/** Eén profielsectie (`### 4/6`) uit een kruisdocument. */
export interface HdCrossProfileMarkdown {
  /** "4/6". */
  readonly key: string;
  readonly markdown: string;
}

/** Een kruisdocument opgedeeld rond de profielsecties. */
export interface HdCrossMarkdownParts {
  /** Alles vóór de eerste profielsectie, inclusief de kop "Per profiel" en de inleidende zin. */
  readonly intro: string;
  readonly profiles: readonly HdCrossProfileMarkdown[];
  /** Alles vanaf de eerste `##`-kop na de profielsecties (schaduw, reflecties). */
  readonly outro: string;
}

const PROFILE_HEADING = /^###\s+([1-6]\/[1-6])\s*$/;
const SECTION_HEADING = /^##\s/;

/**
 * Deelt een kruisdocument op zodat het paneel de profielen als inklapbare
 * secties kan tonen. De documenten volgen één vaste structuur: een
 * `## Per profiel`-sectie met `### 1/3`-subkoppen, gevolgd door de volgende
 * `##`-sectie. Taalonafhankelijk: enkel de profielkoppen worden herkend.
 * Zonder profielkoppen komt het hele document in `intro`.
 */
export function splitHdCrossMarkdown(markdown: string): HdCrossMarkdownParts {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const firstProfile = lines.findIndex(line => PROFILE_HEADING.test(line));
  if (firstProfile < 0) {
    return { intro: markdown.trim(), profiles: [], outro: '' };
  }
  const afterProfiles = lines.findIndex((line, index) => index > firstProfile && SECTION_HEADING.test(line));
  const end = afterProfiles < 0 ? lines.length : afterProfiles;

  const profiles: { key: string; lines: string[] }[] = [];
  for (const line of lines.slice(firstProfile, end)) {
    const heading = PROFILE_HEADING.exec(line);
    if (heading) {
      profiles.push({ key: heading[1], lines: [] });
    } else {
      profiles[profiles.length - 1].lines.push(line);
    }
  }

  return {
    intro: lines.slice(0, firstProfile).join('\n').trim(),
    profiles: profiles.map(profile => ({ key: profile.key, markdown: profile.lines.join('\n').trim() })),
    outro: lines.slice(end).join('\n').trim(),
  };
}
